import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { ProspectNotifyButton } from "@/components/ProspectNotifyButton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, Loader2, Sparkles } from "lucide-react";
import { announceBooking } from "@/components/FirstVisitLocation";
import { toast } from "sonner";

const PKG = [
  { key: "daily", label: "Daily Pass", desc: "One available program day" },
  { key: "weekly", label: "Weekly Pass", desc: "All open days in one week" },
  { key: "monthly", label: "Monthly Pass", desc: "Eligible days in a month" },
];

const TransportPassButton = ({ active, tprice, pct, onPick }) => {
  const f = 1 - pct / 100;
  const one = Math.round(tprice * 5 * f * 100) / 100;
  return (
    <button onClick={onPick} data-testid="pkg-transport-pass"
      className={`mt-2 w-full text-left rounded-xl border-2 p-3 transition-all flex items-center justify-between gap-3 ${active ? "border-gold bg-gold/10" : "border-dashed border-slate-300"}`}>
      <span><p className="font-display font-bold text-navy text-sm">5-Day Transport Pass <span className="ml-1 text-[10px] font-bold text-white bg-coral rounded-full px-2 py-0.5">Save {pct}%</span></p>
        <p className="text-xs text-slate-500">Rides on any 5 school days you pick — ${one} one way or ${one * 2} round trip.</p></span>
      <span className="font-bold text-gold whitespace-nowrap" data-testid="transport-pass-price">from ${one}</span>
    </button>
  );
};

const fmtTime = (t) => { if (!t) return "2:30 PM"; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const hr = h % 12 || 12; return `${hr}:${String(m).padStart(2, "0")} ${ap}`; };
const fmtMoney = (n) => (Number(n) % 1 === 0 ? `$${Number(n)}` : `$${Number(n).toFixed(2)}`);

export default function BookAdventure() {
  const [kids, setKids] = useState([]);
  const [days, setDays] = useState([]);
  const [settings, setSettings] = useState(null);
  const [credits, setCredits] = useState(0);
  const [childId, setChildId] = useState("");
  const [pkg, setPkg] = useState("daily");
  const [selected, setSelected] = useState([]);
  const [addons, setAddons] = useState([]);
  const [promo, setPromo] = useState("");
  const [ebValid, setEbValid] = useState(false);
  const [ebReason, setEbReason] = useState("");
  const [useCredit, setUseCredit] = useState(false);
  const [transport, setTransport] = useState("none");
  const [location, setLocation] = useState("berkeley");
  const [tinfo, setTinfo] = useState({});
  const [schools, setSchools] = useState([]);
  const [perDay, setPerDay] = useState(false);
  const [tpass, setTpass] = useState(false);
  const [tmap, setTmap] = useState({});
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    (async () => {
      const [k, c, s, cr, sch] = await Promise.all([
        api.get("/children"), api.get("/public/calendar"), api.get("/public/settings"), api.get("/credits"), api.get("/schools"),
      ]);
      setKids(k.data); setDays(c.data); setSettings(s.data); setSchools(sch.data);
      setCredits(cr.data.filter((x) => x.status === "available").length);
      if (k.data[0]) setChildId(k.data[0].id);
    })();
  }, []);

  const toggleDay = (iso) => {
    if (pkg === "daily") { setSelected([iso]); return; }
    if (selected.includes(iso)) { setSelected(selected.filter((d) => d !== iso)); return; }
    if (tpass && selected.length >= 5) { toast.error("The Transport Pass covers 5 school days"); return; }
    const cap = pkg === "weekly" ? (settings?.pricing?.weekly_max_days || 5) : (loc === "prospect" ? (settings?.pricing?.prospect_monthly_max_days || 20) : (settings?.pricing?.monthly_max_days || 20));
    if (pkg === "weekly" && selected.length >= cap) { toast.error(`Weekly Pass covers up to ${cap} days`); return; }
    if (pkg === "monthly" && selected.length >= cap) { toast.error(`Monthly Pass covers up to ${cap} days`); return; }
    setSelected([...selected, iso]);
  };
  const selectWeek = (week) => {
    const cap = settings?.pricing?.weekly_max_days || 5;
    const wd = days.filter((d) => d.week === week && d.display === "available" && !pastProspectEnd(d.date)).map((d) => d.date).slice(0, cap);
    setSelected(wd);
  };
  const toggleAddon = (key) => setAddons(addons.includes(key) ? addons.filter((a) => a !== key) : [...addons, key]);

  const checkPromo = async () => {
    const code = promo.trim();
    if (!code) { setEbValid(false); setEbReason(""); return; }
    try {
      const { data } = await api.get(`/early-bird/check?code=${encodeURIComponent(code)}`);
      setEbValid(!!data.valid);
      setEbReason(data.valid ? "" : (data.reason || ""));
    } catch { setEbValid(false); setEbReason(""); }
  };

  const joinWaitlist = async (date) => {
    if (!childId) return toast.error("Select an explorer first");
    try { await api.post("/waitlist", { date, child_id: childId }); toast.success("Added to the waitlist! We'll notify you the moment a spot opens."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const price = settings?.pricing || {};
  const founding = settings?.founding_active;
  const selChild = kids.find((k) => k.id === childId);
  const gold = selChild?.is_gold;
  const isProspect = /prospect/i.test(selChild?.school || "");
  const loc = isProspect ? location : "berkeley";
  const promoCfg = settings?.prospect_promo || {};
  const pEnd = settings?.pricing?.prospect_end_date;
  const pastProspectEnd = (iso) => loc === "prospect" && !!pEnd && iso > pEnd;
  const pEndLabel = pEnd ? new Date(pEnd + "T12:00").toLocaleDateString("en", { month: "long", day: "numeric" }) : "";
  const promoActive = !!settings?.prospect_promo_active;
  const useFounding = founding || ebValid;
  const prospectRate = (pk) => (promoActive && promoCfg[`intro_${pk}`] != null ? promoCfg[`intro_${pk}`] : (price[`prospect_${pk}`] ?? price[pk]) || 0);
  const rate = (pk) => {
    if (loc === "prospect") return prospectRate(pk);
    return ((useFounding ? (price[`founding_${pk}`] ?? price[pk]) : gold ? (price[`gold_${pk}`] ?? price[pk]) : price[pk]) || 0);
  };
  const rateLabel = loc === "prospect" ? (promoActive ? "Founding week rate" : "Prospect in-house rate") : ebValid ? "Early Bird code" : founding ? "Founding Explorer rate" : gold ? "Gold member rate" : null;
  const base = pkg === "transport" ? 0 : rate(pkg);
  const addonTotal = pkg === "transport" ? 0 : (settings?.addons || []).filter((a) => addons.includes(a.key)).reduce((s, a) => s + a.price, 0) * (selected.length || 1);
  const LEGS = { none: 0, pickup: 1, dropoff: 1, both: 2 };
  const tprice = settings?.transport?.price_each_way || 12;
  const tlegs = loc === "prospect" ? 0 : (LEGS[transport] || 0);
  const passPct = settings?.transport?.pass_discount_pct ?? 10;
  const passFactor = tpass ? 1 - passPct / 100 : 1;
  const transportCost = loc === "prospect" ? 0 : Math.round((perDay && !tpass
    ? selected.reduce((sum, d) => sum + (LEGS[tmap[d] || "none"] || 0) * tprice, 0)
    : tlegs * tprice * (selected.length || 1) * passFactor) * 100) / 100;
  const estimate = (useCredit && pkg === "daily" ? 0 : (base || 0)) + addonTotal + transportCost;

  const book = async () => {
    if (!childId) return toast.error("Select an explorer");
    if (!selChild?.waiver?.signed) return toast.error("Please sign the waiver for this explorer first (My Explorers).");
    if (selected.length === 0) return toast.error("Select at least one date");
    if (tpass && selected.length !== 5) return toast.error(`Pick exactly 5 school days for the Transport Pass (${selected.length} chosen)`);
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { child_id: childId, package_type: pkg, dates: selected, addons: pkg === "transport" ? [] : addons, use_credit: pkg === "transport" ? false : useCredit, promo_code: loc === "prospect" ? null : (promo || null), transport: loc === "prospect" ? "none" : transport, transport_info: loc === "prospect" ? {} : tinfo, transport_map: (loc !== "prospect" && perDay && !tpass) ? tmap : {}, location: loc, transport_pass: tpass });
      if (data.payment_order_id) { setOrder(data); }
      else { toast.success("Booked! Your adventure is confirmed."); setSelected([]); }
      if (loc !== "prospect") { announceBooking(); }
    } catch (e) {
      const msg = apiErr(e);
      if (msg.includes("Transportation is full")) {
        for (const d of selected) { try { await api.post("/transport-waitlist", { date: d, child_id: childId, transport }); } catch (err) { console.error("Waitlist add failed:", err); } }
        toast("Transportation was full — we've added you to the transport waitlist and will notify you if space opens.");
      } else { toast.error(msg); }
    }
    finally { setBusy(false); }
  };

  const weeks = [...new Set(days.filter((d) => d.display === "available").map((d) => d.week))];

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><CalendarPlus className="w-6 h-6 text-turquoise" /> Book an Adventure</h1>
      <p className="text-slate-500">Choose your explorer, a package, and the dates.</p>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Label className="text-xs">Explorer</Label>
            <Select value={childId} onValueChange={setChildId}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="book-child-select"><SelectValue placeholder="Select explorer" /></SelectTrigger>
              <SelectContent>{kids.map((k) => <SelectItem key={k.id} value={k.id}>{k.preferred_name || k.first_name} {k.last_name}</SelectItem>)}</SelectContent>
            </Select>
            {kids.length === 0 && <p className="text-coral text-sm mt-2">Add an explorer first from My Explorers.</p>}
            {selChild && !selChild.waiver?.signed && <p className="text-coral text-sm mt-2 font-semibold" data-testid="book-waiver-warning">⚠ Please sign the waiver & media consent for {selChild.preferred_name || selChild.first_name} in <b>My Explorers</b> before booking.</p>}
          </div>

          {isProspect && (
            <div className="bg-white rounded-2xl border-2 border-gold/50 p-5" data-testid="prospect-option">
              <Label className="text-xs mb-2 block">Program location — special for Prospect Preschool families</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                <button onClick={() => setLocation("berkeley")} data-testid="loc-berkeley" className={`text-left rounded-xl border-2 p-3 transition-all ${loc === "berkeley" ? "border-turquoise bg-turquoise/10" : "border-slate-200"}`}>
                  <p className="font-display font-bold text-navy text-sm">At The Berkeley Center</p>
                  <p className="text-xs text-slate-500">Standard on-site after-school program</p>
                </button>
                <button onClick={() => setLocation("prospect")} data-testid="loc-prospect" className={`text-left rounded-xl border-2 p-3 transition-all ${loc === "prospect" ? "border-gold bg-gold/10" : "border-slate-200"}`}>
                  <p className="font-display font-bold text-navy text-sm">In-house at Prospect Preschool {promoActive && <span className="text-gold">· Founding Week</span>}</p>
                  <p className="text-xs text-slate-500">Stay on-site at Prospect — no travel to Berkeley</p>
                  <p className="text-sm font-bold text-gold mt-1" data-testid="prospect-price-line">{fmtMoney(prospectRate("daily"))}/day · {fmtMoney(prospectRate("weekly"))}/week · {fmtMoney(prospectRate("monthly"))}/month</p>
                </button>
              </div>
              {loc === "prospect" && (
                <div className="mt-2 space-y-2" data-testid="prospect-note">
                  <p className="text-[11px] text-navy bg-gold/10 rounded-lg px-3 py-2">🏫 Your explorer stays right at Prospect Preschool for the after-school program. No transportation needed — they're already on-site.</p>
                  {price.prospect_price_end && <p className="text-[11px] text-navy bg-turquoise/10 rounded-lg px-3 py-2" data-testid="prospect-price-end-note">Current in-house prices (${price.prospect_daily}/day · ${price.prospect_weekly}/week · ${price.prospect_monthly}/month) are valid until {new Date(price.prospect_price_end + "T12:00").toLocaleDateString("en", { month: "long", day: "numeric" })}.</p>}
                  {pEnd && <p className="text-[11px] text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="prospect-end-note">The in-house program runs until {pEndLabel}. Dates after {pEndLabel} can't be booked yet because pricing may change.<ProspectNotifyButton /></p>}
                  {promoCfg?.start && promoActive && (
                    <div className="text-[11px] rounded-lg px-3 py-2 border border-dashed border-gold/60 bg-gold/5" data-testid="prospect-promo-note">
                      <b className="text-navy">Founding Week ({new Date(promoCfg.start + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}–{new Date(promoCfg.end + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}):</b> {promoActive ? <>intro pricing (${promoCfg.intro_daily}/day · ${promoCfg.intro_weekly}/week · ${promoCfg.intro_monthly}/month) is active now! 🎉</> : <>intro pricing (${promoCfg.intro_daily}/day · ${promoCfg.intro_weekly}/week · ${promoCfg.intro_monthly}/month) unlocks once {promoCfg.unlock_count} Prospect explorers sign up.
                      <span className="block mt-1 text-slate-500">{settings?.prospect_signups ?? 0}/{promoCfg.unlock_count} signed up so far. Standard rate (${price.prospect_daily}/day) applies until it unlocks.</span></>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Label className="text-xs mb-2 block">Package</Label>
            <div className="grid sm:grid-cols-3 gap-3">
              {PKG.map((p) => (
                <button key={p.key} onClick={() => { setPkg(p.key); setSelected([]); setTpass(false); }} data-testid={`pkg-${p.key}`}
                  className={`text-left rounded-xl border-2 p-3 transition-all ${pkg === p.key ? "border-turquoise bg-turquoise/10" : "border-slate-200"}`}>
                  <p className="font-display font-bold text-navy text-sm">{p.label}</p>
                  <p className="text-xs text-slate-500">{p.desc}</p>
                  <p className="font-bold text-turquoise mt-1">
                    ${rate(p.key)}
                    {rate(p.key) !== price[p.key] && <span className="text-slate-400 line-through ml-1 text-xs">${price[p.key]}</span>}
                  </p>
                </button>
              ))}
            </div>
            <button onClick={() => { setPkg("transport"); setTpass(false); setSelected([]); setLocation("berkeley"); if (transport === "none") setTransport("both"); }} data-testid="pkg-transport"
              className={`mt-3 w-full text-left rounded-xl border-2 p-3 transition-all flex items-center justify-between gap-3 ${pkg === "transport" && !tpass ? "border-gold bg-gold/10" : "border-dashed border-slate-300"}`}>
              <span><p className="font-display font-bold text-navy text-sm">Transport only</p>
                <p className="text-xs text-slate-500">Just the ride — school pickup and/or drop-off to the place you choose. Pick any days below.</p></span>
              <span className="font-bold text-gold whitespace-nowrap">${tprice}/trip</span>
            </button>
            <TransportPassButton active={tpass} tprice={tprice} pct={passPct} onPick={() => { setPkg("transport"); setTpass(true); setPerDay(false); setSelected([]); setLocation("berkeley"); if (transport === "none") setTransport("both"); }} />
            {pkg === "weekly" && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs text-slate-500 self-center">Quick-pick week:</span>
                {weeks.map((w) => <Button key={w} size="sm" variant="outline" className="rounded-full" onClick={() => selectWeek(w)} data-testid={`week-${w}`}>Week {w}</Button>)}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Label className="text-xs mb-2 block">Select dates ({selected.length} chosen)</Label>
            <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto">
              {days.map((d) => {
                const full = d.display === "full";
                const closed = d.display === "closed" || pastProspectEnd(d.date);
                const on = selected.includes(d.date);
                return (
                  <button key={d.date} disabled={closed} onClick={() => (full ? joinWaitlist(d.date) : toggleDay(d.date))} data-testid={`book-day-${d.date}`}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${on ? "bg-turquoise text-navy border-turquoise" : full ? "bg-red-50 border-red-200 text-red-500 hover:bg-red-100" : closed ? "bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed" : "bg-white border-slate-200 text-navy hover:border-turquoise"}`}>
                    {new Date(d.date + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}
                    {full ? <span className="block text-[9px]">Waitlist</span> : d.spaces != null && !closed && <span className="block text-[9px] text-slate-400">{d.spaces} left</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <Label className="text-xs mb-2 block">Optional add-ons (per day)</Label>
            <div className="space-y-2">
              {(settings?.addons || []).map((a) => (
                <label key={a.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={addons.includes(a.key)} onCheckedChange={() => toggleAddon(a.key)} data-testid={`addon-${a.key}`} />
                  <span className="text-navy font-medium">{a.name}</span>
                  <span className="text-slate-400">— {a.desc}</span>
                  <span className="ml-auto font-bold text-turquoise">+${a.price}</span>
                </label>
              ))}
            </div>
          </div>

          {loc !== "prospect" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="transport-card">
            <Label className="text-xs mb-1 block">🚐 Need a lift? Transportation — ${tprice} per child, each way</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {[["none", "No transportation", 0], ["pickup", "School Pickup", tprice], ["dropoff", "After-School Drop-Off", tprice], ["both", "Pickup + Drop-Off", tprice * 2]].map(([k, l, c]) => (
                <button key={k} onClick={() => setTransport(k)} data-testid={`transport-${k}`} className={`text-left rounded-xl border-2 p-3 transition-all ${transport === k ? "border-turquoise bg-turquoise/10" : "border-slate-200"}`}>
                  <p className="font-semibold text-navy text-sm">{l}</p>
                  <p className="text-turquoise font-bold text-sm">{c ? `$${c}/day` : "Free"}</p>
                </button>
              ))}
            </div>
            {selected.length > 1 && !tpass && (
              <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
                <Checkbox checked={perDay} onCheckedChange={(v) => setPerDay(!!v)} data-testid="transport-perday-toggle" />
                Choose transportation per day
              </label>
            )}
            {perDay && !tpass && (
              <div className="mt-2 space-y-2">
                {selected.slice().sort().map((d) => (
                  <div key={d} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-navy">{new Date(d + "T12:00").toLocaleDateString("en", { month: "short", day: "numeric" })}</span>
                    <Select value={tmap[d] || "none"} onValueChange={(v) => setTmap({ ...tmap, [d]: v })}>
                      <SelectTrigger className="rounded-xl h-8 w-40" data-testid={`tmap-${d}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="pickup">Pickup (${tprice})</SelectItem>
                        <SelectItem value="dropoff">Drop-off (${tprice})</SelectItem>
                        <SelectItem value="both">Both (${tprice * 2})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
            {(transport === "pickup" || transport === "both" || perDay) && (
              <div className="mt-3 space-y-2">
                <Select value={tinfo.school_id || ""} onValueChange={(v) => { const sc = schools.find((s) => s.id === v); setTinfo({ ...tinfo, school_id: v, school_name: v === "other" ? "" : sc?.name }); }}>
                  <SelectTrigger className="rounded-xl" data-testid="transport-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                  <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}<SelectItem value="other">My school is not listed</SelectItem></SelectContent>
                </Select>
                {tinfo.school_id === "other" && <Input placeholder="Your school name (for approval)" onChange={(e) => setTinfo({ ...tinfo, school_name: e.target.value })} className="rounded-xl" data-testid="transport-school-other" />}
                <Input placeholder="School dismissal time" onChange={(e) => setTinfo({ ...tinfo, dismissal_time: e.target.value })} className="rounded-xl" data-testid="transport-dismissal" />
                <Input placeholder="Teacher / class / pickup notes (optional)" onChange={(e) => setTinfo({ ...tinfo, pickup_notes: e.target.value })} className="rounded-xl" data-testid="transport-pickup-notes" />
                <Input placeholder="Pick up from somewhere other than school? Enter place name + address" onChange={(e) => setTinfo({ ...tinfo, pickup_location: e.target.value })} className="rounded-xl" data-testid="transport-pickup-location" />
              </div>
            )}
            {(transport === "dropoff" || transport === "both" || perDay) && (
              <div className="mt-3 space-y-2">
                <div className="bg-turquoise/10 border border-turquoise/30 rounded-xl p-3 text-[11px] text-navy leading-relaxed" data-testid="dropoff-info">
                  🚐 Our mini-buses are large. Your child is dropped at the <b>nearest bus stop</b> to your home — or right to your <b>driveway / front door</b> if the bus can fit. A staff member rides along on every route to make drop-off seamless and safe.
                </div>
                <Label className="text-xs">How should we drop off your explorer?</Label>
                <div className="grid gap-2">
                  {[["collected_stop", "An adult will collect them from the nearest bus stop"], ["walk_home", "They may walk home from the nearest bus stop"], ["door", "I know the bus can fit — drop right at our door"]].map(([k, l]) => (
                    <button type="button" key={k} onClick={() => setTinfo({ ...tinfo, dropoff_preference: k })} data-testid={`dropoff-pref-${k}`} className={`text-left rounded-xl border-2 px-3 py-2 text-sm transition-colors ${tinfo.dropoff_preference === k ? "border-turquoise bg-turquoise/10 text-navy font-semibold" : "border-slate-200 text-slate-600 hover:border-turquoise/50"}`}>{l}</button>
                  ))}
                </div>
                <Input placeholder="Approved drop-off address" onChange={(e) => setTinfo({ ...tinfo, dropoff_address: e.target.value })} className="rounded-xl" data-testid="transport-dropoff-addr" />
                <Input placeholder="Receiving adult name" onChange={(e) => setTinfo({ ...tinfo, receiving_person: e.target.value })} className="rounded-xl" data-testid="transport-receiver" />
                <Input placeholder="Receiving adult phone" onChange={(e) => setTinfo({ ...tinfo, receiving_phone: e.target.value })} className="rounded-xl" data-testid="transport-receiver-phone" />
                <p className="text-[11px] text-slate-400">Children are only released to an authorized adult listed on the child's account.</p>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-2">Transportation is subject to route and space availability.</p>
            <p className="text-[11px] text-gold font-semibold mt-1" data-testid="transport-cutoff-note">⏰ Same-day transport closes at {fmtTime(settings?.transport?.cutoff_time)} (Bermuda) so drivers can prepare the buses. Add transport for upcoming days anytime.</p>
          </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-navy text-white rounded-2xl p-6 h-fit lg:sticky lg:top-20" data-testid="book-summary">
          <p className="font-display font-bold text-lg">Booking Summary</p>
          {rateLabel && <span className="inline-block mt-1 text-[11px] font-bold text-gold bg-gold/15 rounded-full px-2 py-0.5" data-testid="rate-badge">{rateLabel} applied</span>}
          <div className="mt-4 space-y-2 text-sm text-white/70">
            <div className="flex justify-between"><span>Package</span><span className="text-white font-semibold" data-testid="summary-package">{tpass ? "5-Day Transport Pass" : pkg === "transport" ? "Transport only" : PKG.find((p) => p.key === pkg)?.label}</span></div>
            <div className="flex justify-between"><span>Dates</span><span className="text-white font-semibold">{selected.length}</span></div>
            <div className="flex justify-between"><span>Base</span><span className="text-white">${base || 0}</span></div>
            <div className="flex justify-between"><span>Add-ons</span><span className="text-white">${addonTotal}</span></div>
            {tlegs > 0 && <div className="flex justify-between"><span>Transport ({selected.length}× ${tlegs * tprice}){tpass ? ` · ${passPct}% off` : ""}</span><span className="text-white" data-testid="summary-transport">${transportCost}</span></div>}
          </div>
          {credits > 0 && pkg === "daily" && (
            <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer bg-white/10 rounded-xl p-3">
              <Checkbox checked={useCredit} onCheckedChange={(v) => setUseCredit(!!v)} data-testid="use-credit-checkbox" />
              <Sparkles className="w-4 h-4 text-gold" /> Use 1 Adventure Credit ({credits} available)
            </label>
          )}
          <div className="mt-4">
            <Label className="text-xs text-white/60">Promo / Early Bird code</Label>
            <Input value={promo} onChange={(e) => { setPromo(e.target.value); setEbValid(false); setEbReason(""); }} onBlur={checkPromo} className="rounded-xl bg-white/10 border-white/20 text-white mt-1" data-testid="promo-input" />
            {ebValid && <p className="text-[11px] text-emerald-300 mt-1 font-semibold" data-testid="promo-eb-ok">✅ Early Bird price applied — you're saving on this booking!</p>}
            {ebReason && <p className="text-[11px] text-coral mt-1" data-testid="promo-eb-err">{ebReason}</p>}
          </div>
          <div className="flex justify-between items-center mt-5 pt-4 border-t border-white/10">
            <span className="text-white/70">Estimated total</span>
            <span className="font-display font-extrabold text-2xl text-gold">${estimate.toFixed(2)}</span>
          </div>
          <Button onClick={book} disabled={busy} className="w-full mt-4 bg-gold hover:bg-gold/90 text-navy font-bold rounded-full" data-testid="confirm-booking-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book Adventure"}
          </Button>
          <p className="text-[11px] text-white/50 mt-2 text-center">Payment must be completed before your child arrives for after-school or camp. Gold members are confirmed first.</p>
        </div>
      </div>

      <PaymentDialog order={order} open={!!order} onClose={() => { setOrder(null); setSelected([]); }} />
    </div>
  );
}
